/**
 * FormulaOptions — "Suggest a formula" that offers several choices (#67).
 *
 * Calls a suggest-formula endpoint (items or places) with the month the owner
 * is looking at, so a daily spend in February reads 200*28 and a weekly one in a
 * five-Friday month 250*5, then shows every returned option as a clickable chip
 * ("200*28 = R5 600 · daily: 28 days in February"). Clicking a chip hands its
 * formula to `onPick`; nothing is saved here, the host decides. Undo restores
 * whatever the host had before the first pick.
 *
 * AI goes through the one gate: while AI is on the owner is asked for AI
 * permission first (nothing leaves the browser before that). When AI is off,
 * or permission is declined, the same button still works by asking the server
 * for its rule-based options (`rules_only`), so no "AI couldn't run" notice is
 * raised for a request that never wanted AI.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useState } from 'react';
import { api } from '../api.js';
import { fmtR } from '../money.js';
import { ensureAIConsent, useAI } from './AIGate.jsx';
import { useAuth } from '../App.jsx';

/**
 * @param {object} props
 * @param {string} props.endpoint  '/budget/items/suggest_formula/' or the places one.
 * @param {object} props.body      {title, amount, note?, item?, month?} sent as-is.
 * @param {(calc: string) => void} props.onPick  called with the chosen formula.
 * @param {() => void} [props.onUndo]  restores the host's previous value.
 * @param {boolean} [props.disabled]
 * @param {string} [props.label]
 */
export default function FormulaOptions({ endpoint, body, onPick, onUndo, disabled, label = 'Suggest a formula' }) {
  const [state, setState] = useState('idle'); // idle | working | shown
  const [options, setOptions] = useState([]);
  const [picked, setPicked] = useState(null);
  const [line, setLine] = useState('');
  const ai = useAI();
  const { me } = useAuth() || {};

  const go = async () => {
    if (state === 'working') return;
    // Only ask for AI permission when AI is actually on; a declined sheet
    // still gets the rule-based options rather than a dead button.
    let rulesOnly = !ai.on;
    if (!rulesOnly && !(await ensureAIConsent(me))) rulesOnly = true;
    setState('working'); setLine(''); setPicked(null);
    try {
      const d = await api(endpoint, { method: 'POST', body: { ...body, rules_only: rulesOnly } });
      const opts = Array.isArray(d?.options) ? d.options
        : (d?.suggestion || d?.formula) ? [{ calculation: d.suggestion || d.formula, amount: null, reason: '' }] : [];
      setOptions(opts);
      setLine(opts.length ? `Pick one for ${monthWord(d?.month)} — it goes into the formula box.`
                          : 'No formula fits this one — type it instead.');
      setState(opts.length ? 'shown' : 'idle');
    } catch (e) {
      setLine(e?.data?.detail || 'Could not suggest a formula — type it instead.');
      setState('idle');
    }
  };

  const pick = (o) => { setPicked(o.calculation); onPick?.(o.calculation); };
  const undo = () => { setPicked(null); onUndo?.(); };

  return (
    <span className="inline-flex flex-col items-start gap-1 w-full">
      <button type="button" disabled={disabled || state === 'working'} onClick={go}
              title="Suggest rate × count formulas counted on this month's real days and weekdays"
              className="inline-flex items-center gap-1.5 rounded-xl border transition disabled:opacity-50 px-2.5 py-1.5 text-xs"
              style={{ borderColor: 'var(--color-accent)', color: 'var(--color-accent)' }}>
        <span aria-hidden>{state === 'working' ? '…' : (ai.on ? '✨' : '±')}</span>
        {state === 'working' ? 'Counting…' : label}
      </button>
      {state === 'shown' && (
        <span className="flex flex-wrap gap-1.5" data-guide="formula-options">
          {options.map((o) => (
            <button key={o.calculation} type="button" onClick={() => pick(o)}
                    className={`text-[11px] px-2 py-1 rounded-full border text-left ${
                      picked === o.calculation ? 'border-vblue text-vblue bg-vblue/10' : 'border-edge text-slate-300'}`}>
              {o.calculation}{o.amount != null ? ` = ${fmtR(o.amount)}` : ''}{o.reason ? ` · ${o.reason}` : ''}
            </button>
          ))}
        </span>
      )}
      {line && (
        <span className="text-xs flex items-center gap-2" style={{ color: 'var(--color-accent-400)' }}>
          <span className="min-w-0">{picked ? `Formula: ${picked}. Edit it, or Undo.` : line}</span>
          {picked && onUndo && (
            <button type="button" onClick={undo} className="underline shrink-0"
                    style={{ color: 'var(--color-text)' }}>Undo</button>
          )}
        </span>
      )}
    </span>
  );
}

/** "February" for "2027-02"; "this month" when the server sent no month. */
function monthWord(month) {
  if (!month) return 'this month';
  const [y, m] = String(month).split('-').map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString('en-ZA', { month: 'long' });
}
